import { Product, categoryConfig, ProductStatus } from '../../../mocks/products';
import { agencyInfo } from '../../../mocks/dashboard';

const STATUS_STYLES: Record<ProductStatus, string> = {
  Active: 'bg-emerald-100 text-emerald-700',
  Inactive: 'bg-gray-100 text-gray-500',
  'Sold Out': 'bg-rose-100 text-rose-600',
};

interface ProductCardProps {
  product: Product;
  view: 'grid' | 'list';
  onView: (p: Product) => void;
}

export default function ProductCard({ product, view, onView }: ProductCardProps) {
  const brand = agencyInfo.brandColor;
  const cfg = categoryConfig[product.category];
  const margin = Math.round(((product.sellPrice - product.costPrice) / product.sellPrice) * 100);

  const fmtPrice = (n: number) =>
    `${product.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;

  const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(product.rating));

  if (view === 'list') {
    return (
      <div
        onClick={() => onView(product)}
        className="bg-white border border-gray-100 rounded-2xl flex items-center gap-5 p-4 cursor-pointer hover:border-gray-200 transition-all group"
      >
        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
              <i className={`${cfg.icon} text-xs`} />
              {product.category}
            </span>
            {product.featured && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Featured</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-auto ${STATUS_STYLES[product.status]}`}>
              {product.status}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 text-sm truncate">{product.name}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{product.destination} · {product.duration}</p>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
          <p className="font-bold text-gray-900">{fmtPrice(product.sellPrice)}</p>
          <p className="text-xs text-gray-400">Cost: {fmtPrice(product.costPrice)}</p>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${margin >= 25 ? 'bg-emerald-100 text-emerald-700' : margin >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-600'}`}>
            {margin}% margin
          </span>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
          <div className="text-center">
            <p className="font-semibold text-gray-700 text-sm">{product.timesBooked}</p>
            <p>bookings</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-gray-700 text-sm">{product.rating}</p>
            <p>rating</p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer" onClick={(e) => { e.stopPropagation(); onView(product); }}>
            <i className="ri-eye-line text-sm" />
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <i className="ri-edit-line text-sm" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onView(product)}
      className="bg-white border border-gray-100 rounded-2xl overflow-hidden cursor-pointer hover:border-gray-200 transition-all group hover:translate-y-[-2px]"
    >
      {/* Image */}
      <div className="relative w-full h-44 overflow-hidden bg-gray-100">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        {/* Badges overlay */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-white/90 backdrop-blur-sm" style={{ color: cfg.color }}>
            <i className={`${cfg.icon} text-xs`} />
            {product.category}
          </span>
          {product.featured && (
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-500 text-white">
              <i className="ri-star-fill text-xs" />
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[product.status]}`}>
            {product.status}
          </span>
        </div>
        {/* Price at bottom */}
        <div className="absolute bottom-3 right-3 text-right">
          <p className="text-white font-bold text-base leading-tight">{fmtPrice(product.sellPrice)}</p>
          <p className="text-white/70 text-xs">{product.minPax > 1 ? `from ${product.minPax} pax` : 'per pax'}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-0.5 line-clamp-1">{product.name}</h3>
        <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
          <i className="ri-map-pin-line" />
          {product.destination} · {product.duration}
        </p>

        {/* Stars */}
        <div className="flex items-center gap-1 mb-3">
          {stars.map((filled, i) => (
            <i key={i} className={`text-xs ${filled ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-200'}`} />
          ))}
          <span className="text-xs text-gray-400 ml-1">{product.rating} · {product.timesBooked} booked</span>
        </div>

        {/* Pricing row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div>
            <p className="text-xs text-gray-400">Cost: {fmtPrice(product.costPrice)}</p>
            <p className="text-xs font-medium text-gray-500">
              Profit: {fmtPrice(product.sellPrice - product.costPrice)}
            </p>
          </div>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${margin >= 25 ? 'bg-emerald-100 text-emerald-700' : margin >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-600'}`}>
            {margin}% margin
          </span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {product.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
